import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { Box, Button, Menu, MenuItem, useTheme } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import InfoIcon from '@mui/icons-material/Info'
import EditIcon from '@mui/icons-material/Edit'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Autoclave, OpenAutoclaveModal, OpenSporetestModal, SporetestingResult } from '../types'
import { Biobox } from '../../../../types'

type AutoclaveMenu = {
  autoclave: Autoclave,
  bioboxes: Biobox[],
  sporetestsByAutoclaveId: Map<number, SporetestingResult[]>,
  setOpenSporetestModal: Dispatch<SetStateAction<OpenSporetestModal>>,
  setOpenAutoclaveModal: Dispatch<SetStateAction<OpenAutoclaveModal>>,
  setOpenAutoclaveStatusModal: Dispatch<SetStateAction<boolean>>,
  setOpenDeleteAutoclaveModal: Dispatch<SetStateAction<boolean>>,
  setOpenEnvelopeInstructionsModal: Dispatch<SetStateAction<boolean>>,
}

const AutoclaveMenu = (props: AutoclaveMenu) => {
  const {
    autoclave,
    bioboxes,
    sporetestsByAutoclaveId,
    setOpenSporetestModal,
    setOpenAutoclaveModal,
    setOpenAutoclaveStatusModal,
    setOpenDeleteAutoclaveModal,
    setOpenEnvelopeInstructionsModal,
  } = props

  const theme = useTheme()

  const autoclaveTestResults = sporetestsByAutoclaveId.get(Number(autoclave.autoclave_id)) ?? []

  const hasSporeTests = useMemo(() => {
    return Boolean(autoclaveTestResults.length)
  }, [autoclave])

  const hasActiveMesaBoxes = useMemo(() => {
    return Boolean(bioboxes.filter(box => box.active && box.box_type === 'mesa').length)
  }, [bioboxes])  

  const [optionsEl, setOptionsEl] = useState<HTMLElement | null>(null)

  const handleOptionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOptionsEl(event.currentTarget)
  }

  const handleOptionsClose = () => {
    setOptionsEl(null)
  }

  const handleEditAutoclave = () => {
    setOpenAutoclaveModal({ open: true, autoclave })
  }

  const handleAutoclaveStatus = () => {
    setOpenAutoclaveStatusModal(true)
  }

  const handleDeleteAutoclave = () => {
    setOpenDeleteAutoclaveModal(true)
  }

  const handleOpenSporeTestModal = (type: string) => {
    setOpenSporetestModal({
      open: true,
      autoclaveId: Number(autoclave.autoclave_id),
      testType: type,
      sporetest: null,
    })
  }

  const handleEnvelopeDirections = () => {
    setOpenEnvelopeInstructionsModal(true)
  }

  const menuOpen = Boolean(optionsEl)

  return (
    <Box>
      <Button aria-haspopup="true" variant="contained" name="export" onClick={handleOptionsClick}>
        Edit/Options <FontAwesomeIcon icon="caret-down" size="sm" className="m-l-8" />
      </Button>
      <Menu
        id="account-menu"
        anchorEl={optionsEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        open={menuOpen}
        onClose={handleOptionsClose}
        onClick={handleOptionsClose}
        MenuListProps={{
          'aria-labelledby': 'export-button'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuItem onClick={handleEditAutoclave} sx={{ color: theme.palette.color50555b.main }}>
          <EditIcon fontSize="small" />
          <span className="m-l-8">Edit Autoclave</span>
        </MenuItem>
        <MenuItem onClick={handleAutoclaveStatus} sx={{ color: theme.palette.color50555b.main }}>
          {autoclave.active ? <RemoveCircleIcon fontSize="small" /> : <AddCircleIcon fontSize="small" />}
          <span className="m-l-8">{autoclave.active ? 'Deactivate' : 'Activate'} Autoclave</span>
        </MenuItem>
        {!hasSporeTests && (
          <MenuItem onClick={handleDeleteAutoclave} sx={{ color: theme.palette.color50555b.main }}>
            <DeleteForeverIcon fontSize="small" />
            <span className="m-l-8">Delete Autoclave</span>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleOpenSporeTestModal('inoffice')} sx={{ color: theme.palette.color50555b.main }}>
          <AddIcon fontSize="small" />
          <span className="m-l-8">Log In-Office Spore Test</span>
        </MenuItem>
        {hasActiveMesaBoxes && (
          <MenuItem onClick={() => handleOpenSporeTestModal('mesa')} sx={{ color: theme.palette.color50555b.main }}>
            <AddIcon fontSize="small" />
            <span className="m-l-8">Log Mail-In Spore Test</span>
          </MenuItem>
        )}
        <MenuItem onClick={handleEnvelopeDirections} sx={{ color: theme.palette.color50555b.main }}>
          <InfoIcon fontSize="small" />
          <span className="m-l-8">How to Fill Out Your Envelope</span>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default AutoclaveMenu
